import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PagedResponse } from '@models/paged-response';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { ZbpDataTableComponent } from '@shared/zbp-data-table/zbp-data-table.component';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import {
  StandardsDetailReport,
  StandardsDetailDetail,
} from './shared-standards-detail-report.model';
import PageChangeEvent = ZbpDataTable.PageChangeEvent;
import SIMPLE_COLUMN = ZbpDataTable.SIMPLE_COLUMN;
import TEXT_CELL = ZbpDataTable.TEXT_CELL;
import PILL_CELL = ZbpDataTable.PILL_CELL;

/**
 * Standards Detail Report Table to be shared between Admins and Teachers.
 */

@Component({
  selector: 'zbp-shared-standards-detail-report',
  imports: [
    NgSelectModule,
    NgbModule,
    ZbpDataTableComponent,
  ],
  templateUrl: './shared-standards-detail-report.component.html',
  styleUrl: './shared-standards-detail-report.component.scss'
})
export class SharedStandardsDetailComponent implements OnChanges {
  @Input() reports: PagedResponse<StandardsDetailReport[]>;
  @Input() isLoading: boolean;
  @Input() noDataMessage: string;
  @Output() pageChange: EventEmitter<PageChangeEvent> = new EventEmitter<PageChangeEvent>();

  columns: ZbpDataTable.Column[] = [
    {
      type: SIMPLE_COLUMN,
      key: 'skill',
      header: 'Skill',
      textAlign: 'center',
      columnWidth: '20%',
    },
    {
      type: SIMPLE_COLUMN,
      key: 'description',
      header: 'Description of Standard',
      textAlign: 'center',
    },
    {
      type: SIMPLE_COLUMN,
      key: 'average',
      header: 'Class Average',
      textAlign: 'center',
    },
  ];
  rows: ZbpDataTable.Row[] = [];
  page = 1;
  pageSize = 25;
  collectionSize = 0;
  numberOfDisplayedRecords: number;
  statusMessage = '';

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reports?.currentValue) {
      this.collectionSize = 0;
      this.rows = [];
      this.statusMessage = '';
      let resultsMessage = '';

      const reports = changes.reports.currentValue as PagedResponse<StandardsDetailReport[]>;

      const [report] = reports.response;
      if (report) {
        this.pageSize = reports.pageSize;
        this.page = reports.pageNumber;
        this.collectionSize = reports.totalRecords;
        this.rows = this.buildRows(report);
        resultsMessage = `${this.numberOfDisplayedRecords} ${this.numberOfDisplayedRecords === 1 ? 'result' : 'results'} found.`;
      } else {
        resultsMessage = 'No results found.';
      }

      setTimeout(() => {
        this.statusMessage = resultsMessage;
      }, 100);
    }
  }

  onPageChange(event: PageChangeEvent): void {
    this.page = event.page;
    this.pageChange.emit(event);
  }

  private buildRows(report: StandardsDetailReport): ZbpDataTable.Row[] {
    const newRows: ZbpDataTable.Row[] = [];
    this.numberOfDisplayedRecords = 0;
    report.benchmarkResults.forEach((results) => {
      newRows.push({
        cells: {
          skill: {
            type: TEXT_CELL,
            text: results.topLevelStandardText,
            textAlign: 'right',
            fontWeight: 700,
            borderRight: 'solid 1px var(--brand-neutrals-warm-grey-W80)',
            borderBottom: 'none',
          },
          description: {
            type: TEXT_CELL,
            text: '',
            backgroundColor: 'var(--brand-table-dark-background)',
            borderBottom: 'none',
          },
          average: {
            type: TEXT_CELL,
            text: '',
            backgroundColor: 'var(--brand-table-dark-background)',
            borderBottom: 'none',
          }
        }
      });

      this.numberOfDisplayedRecords += results.details.length;

      results.details.forEach((detail, index) => {
        newRows.push(this.buildRow(detail, results.details.length === index + 1));
      });
    });
    return newRows;
  }

  private buildRow(detail: StandardsDetailDetail, last: boolean): ZbpDataTable.Row {
    const pillColors = this.getPillColors(detail.standardAverage);
    const newCells: Record<string, ZbpDataTable.Cell> = {
      skill: {
        type: TEXT_CELL,
        text: `${detail.standardNumber} -`,
        textAlign: 'right',
        borderRight: 'solid 1px var(--brand-neutrals-warm-grey-W80)',
        borderBottom: last ? '' : 'none',
      },
      description: {
        type: TEXT_CELL,
        text: detail.standardText,
        backgroundColor: 'var(--brand-table-dark-background)',
        borderBottom: last ? '' : 'none',
      },
      average: {
        type: PILL_CELL,
        text: detail.standardAverageFormatted,
        backgroundColor: pillColors.backgroundColor,
        borderColor: pillColors.borderColor,
        justifyItems: 'center',
        cellBackgroundColor: 'var(--brand-table-dark-background)',
        borderBottom: last ? '' : 'none',
        pillWidth: '100%',
        lineHeight: '1.5rem',
      }
    };

    return { cells: newCells };
  }

  private getPillColors(average: number) {
    if (average < 70) {
      return { backgroundColor: 'var(--brand-red-300)', borderColor: 'var(--brand-red-500)' };
    }

    if (average >= 90) {
      return { backgroundColor: 'var(--brand-green-300)', borderColor: 'var(--brand-green-500)' };
    }

    return { backgroundColor: 'var(--brand-cream-300)', borderColor: 'var(--brand-cream-500)' };
  }
}
